// utils/captureDownload.ts
import domtoimage from 'dom-to-image-more';

/** a.click()로 안전하게 다운로드 */
export const downloadViaAnchor = (url: string, name: string) => {
    const a = document.createElement('a');
    a.href = url;
    a.download = name;
    a.rel = 'noopener';
    a.style.display = 'none';
    document.body.appendChild(a);
    setTimeout(() => {
        a.click();
        document.body.removeChild(a);
    }, 0);
};

/** 폰트/레이아웃 안정화 대기 */
export const settleLayout = async () => {
    try {
        if ((document as any).fonts?.ready) {
            await (document as any).fonts.ready;
        }
    } catch {}
    await new Promise((r) => requestAnimationFrame(() => r(null)));
    await new Promise((r) => setTimeout(r, 0));
};

/** 특정 루트 내 <img>가 모두 로드될 때까지 대기 (타임아웃 기본 15초) */
export const waitForImages = (root: HTMLElement, timeoutMs = 15000): Promise<boolean> => {
    return new Promise((resolve) => {
        const imgs = Array.from(root.querySelectorAll('img'));
        if (imgs.length === 0) return resolve(true);

        const pending = imgs.filter((img) => !(img.complete && img.naturalWidth > 0));
        if (pending.length === 0) return resolve(true);

        let done = 0;
        let ok = true;
        const finish = () => {
            done += 1;
            if (done === pending.length) resolve(ok);
        };
        const onLoad = () => finish();
        const onError = () => {
            ok = false;
            finish();
        };

        pending.forEach((img) => {
            img.addEventListener('load', onLoad, { once: true });
            img.addEventListener('error', onError, { once: true });
        });

        const tid = window.setTimeout(() => {
            pending.forEach((img) => {
                img.removeEventListener('load', onLoad);
                img.removeEventListener('error', onError);
            });
            resolve(false);
        }, timeoutMs);

        const poll = () => {
            if (done === pending.length) clearTimeout(tid);
            else requestAnimationFrame(poll);
        };
        requestAnimationFrame(poll);
    });
};

export const captureNodeToBlob = async (node: HTMLElement, bg = '#fff'): Promise<Blob> => {
    const scale = 3;
    const blob = await domtoimage.toBlob(node, {
        bgcolor: bg,
        quality: 1,
        style: {
            transform: `scale(${scale})`,
            transformOrigin: 'top left',
            width: node.scrollWidth + 'px',
            height: node.scrollHeight + 'px',
        },
        width: node.scrollWidth * scale,
        height: node.scrollHeight * scale,
    });
    return blob;
};

/**
 * 이미지 준비 여부 체크 → 준비되면 캡처 후 다운로드
 * - 준비 전이면 true/false 반환으로 알려주고, 알림 띄우는 건 호출처에서.
 */
export const downloadNodeIfReady = async (
    node: HTMLElement,
    fileName: string,
    options?: { background?: string; timeoutMs?: number }
): Promise<{ ok: boolean; reason?: 'images_not_ready' | 'capture_failed' }> => {
    const { background = '#fff', timeoutMs = 15000 } = options || {};

    // 1) 이미지 로드 확인
    const ready = await waitForImages(node, timeoutMs);
    if (!ready) {
        return { ok: false, reason: 'images_not_ready' };
    }

    await settleLayout();

    try {
        const blob = await captureNodeToBlob(node, background);
        const url = URL.createObjectURL(blob);
        downloadViaAnchor(url, fileName);

        setTimeout(() => URL.revokeObjectURL(url), 0);
        return { ok: true };
    } catch {
        return { ok: false, reason: 'capture_failed' };
    }
};
